export const tangentTriangle = [
  { x: 300, y: 42 },
  { x: 526, y: 444 },
  { x: 74, y: 444 },
]
export const tangentRadius = 78
export const tangentSpacing = (Math.PI * 2) / 24
export const tangentAngles = Array.from({ length: 24 }, (_, i) => -Math.PI / 2 + i * tangentSpacing)

type Point = { x: number; y: number }
export type TangentPointer = Point & { phi: number; pull: number; inside: boolean }

export function createTangentMotion() {
  return {
    x: 0,
    y: 0,
    clock: 0,
    lines: tangentAngles.map((base) => ({ base, offset: 0, velocity: 0, heat: 0 })),
  }
}

export function tangentPointer(point: Point): TangentPointer {
  const dx = point.x - 289
  const dy = point.y - 303
  const distance = Math.hypot(dx, dy)
  return {
    x: clamp(dx / 240, -1, 1),
    y: clamp(dy / 200, -1, 1),
    phi: Math.atan2(dy, dx),
    pull: clamp(distance / 110, 0, 1) * Math.exp(-Math.max(0, distance - 250) / 340),
    inside: true,
  }
}

// Each segment is an exact tangent to the shared circle, clipped to the triangle.
export function tangentSegment(center: Point, angle: number) {
  const nx = Math.cos(angle)
  const ny = Math.sin(angle)
  const points = tangentTriangle
    .flatMap((a, i) => {
      const b = tangentTriangle[(i + 1) % tangentTriangle.length]
      const da = nx * (a.x - center.x) + ny * (a.y - center.y) - tangentRadius
      const db = nx * (b.x - center.x) + ny * (b.y - center.y) - tangentRadius
      if (Math.abs(da) < 1e-8 && Math.abs(db) < 1e-8) return [a, b]
      if (da * db > 0 || Math.abs(da - db) < 1e-8) return []
      const t = da / (da - db)
      if (t < 0 || t > 1) return []
      return [{ x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) }]
    })
    .filter((point, i, all) => all.findIndex((other) => Math.hypot(point.x - other.x, point.y - other.y) < 1e-6) === i)
  if (points.length < 2) return
  // A vertex belongs to two edges. Choose the farthest distinct pair so a
  // tangent through that vertex cannot collapse into a zero-length segment.
  return points
    .flatMap((a, i) => points.slice(i + 1).map((b) => ({ a, b })))
    .sort((a, b) => Math.hypot(b.a.x - b.b.x, b.a.y - b.b.y) - Math.hypot(a.a.x - a.b.x, a.a.y - a.b.y))[0]
}

export function stepTangentMotion(
  motion: ReturnType<typeof createTangentMotion>,
  elapsed: number,
  input: { pointer: TangentPointer; loading: boolean; subdued: boolean },
) {
  const dt = clamp(elapsed, 0, 0.034)
  if (input.loading) motion.clock += dt
  const theta = (motion.clock * Math.PI * 2) / 7
  const mix = input.pointer.inside ? 0.28 : 0
  // Blend directions on the circle: interpolating wrapped angles would jump
  // when the loading phase passes the direction opposite the pointer.
  const phi = input.loading
    ? Math.atan2(
        Math.sin(theta) * (1 - mix) + Math.sin(input.pointer.phi) * mix,
        Math.cos(theta) * (1 - mix) + Math.cos(input.pointer.phi) * mix,
      )
    : input.pointer.phi
  const pull = input.loading
    ? 0.55 + 0.12 * Math.sin(theta * 2) + input.pointer.pull * mix
    : input.pointer.pull * (input.subdued ? 0.62 : 1)
  const x = input.loading ? Math.cos(theta) * 0.14 : input.pointer.x
  const y = input.loading ? Math.sin(theta) * 0.14 : input.pointer.y
  const ease = 1 - Math.exp(-dt / 0.3)
  motion.x += (x - motion.x) * ease
  motion.y += (y - motion.y) * ease
  const steps = Math.max(1, Math.ceil(dt / 0.008))
  const step = dt / steps
  Array.from({ length: steps }).forEach(() => {
    // Read all neighbors before updating any line to preserve both directions.
    const rates = motion.lines.map((line, i) => {
      const delta = wrap(phi - line.base)
      const local = Math.exp(4 * (Math.cos(delta) - 1))
      const goal = (tangentSpacing * 0.3 * pull * Math.sin(delta) * local) / 0.294
      const neighbors = motion.lines[(i + 23) % 24].offset + motion.lines[(i + 1) % 24].offset - 2 * line.offset
      return (goal - line.offset + 0.3 * neighbors) / (0.115 + 0.26 * (1 - local))
    })
    motion.lines.forEach((line, i) => {
      line.velocity = rates[i]
      line.offset = clamp(line.offset + line.velocity * step, -tangentSpacing * 0.42, tangentSpacing * 0.42)
    })
  })
  const changing = motion.lines
    .map((line) => {
      const local = Math.exp(4 * (Math.cos(wrap(phi - line.base)) - 1))
      const heat = pull * local
      line.heat += (heat - line.heat) * (1 - Math.exp(-dt / (0.1 + 0.22 * (1 - local))))
      return Math.abs(line.velocity) > 0.00015 || Math.abs(line.heat - heat) > 0.0004
    })
    .some(Boolean)
  return input.loading || Math.abs(x - motion.x) + Math.abs(y - motion.y) > 0.0004 || changing
}

function clamp(value: number, low: number, high: number) {
  return Math.max(low, Math.min(high, value))
}

function wrap(value: number) {
  return Math.atan2(Math.sin(value), Math.cos(value))
}
